import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion as m } from "framer-motion";
import ap1 from "../images/ap1.jpeg";
import ap2 from "../images/ap2.jpeg";
import ap3 from "../images/ap3.jpeg";
import apg1 from "../images/apg1.jpeg";
import apg2 from "../images/apg2.jpeg";
import apg3 from "../images/apg3.jpeg";
import apg4 from "../images/apg4.jpeg";
import apg5 from "../images/apg5.jpeg";
import apg6 from "../images/apg6.jpeg";
import apg7 from "../images/apg7.jpeg";
import apg8 from "../images/apg8.jpeg";
import apg9 from "../images/apg9.jpeg";
import apg10 from "../images/apg10.jpeg";
import apHero from "../images/apHero.jpeg";

const HERO_IMAGE = apHero;
const SECTION_IMAGES = {
  philosophy: ap1,
  trainingEmphasis: ap2,
  giNoGi: ap3,
};

const GALLERY_IMAGES = [
  apg1, apg2, apg3, apg4, apg5, apg6, apg7, apg8, apg9, apg10,
];

const AdultProgram = () => {
  const [activeImg, setActiveImg] = useState(null);

  const openModal = (img) => setActiveImg(img);
  const closeModal = () => setActiveImg(null);

  // --- Auto-scroll gallery ---
  const galleryRef = useRef(null);
  const isUserInteractingRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);

  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;

    const PX_PER_SEC = 18;

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (!isUserInteractingRef.current) {
        el.scrollLeft += PX_PER_SEC * dt;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll > 0 && el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    const pauseThenResume = () => {
      isUserInteractingRef.current = true;
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 1500);
    };

    el.addEventListener("pointerdown", pauseThenResume, { passive: true });
    el.addEventListener("pointerup", pauseThenResume, { passive: true });
    el.addEventListener("wheel", pauseThenResume, { passive: true });
    el.addEventListener("touchstart", pauseThenResume, { passive: true });
    el.addEventListener("touchmove", pauseThenResume, { passive: true });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  return (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.75 }}
    >
      <div className="flex justify-center pt-28 pb-20">
        <div className="bg-black text-white w-full max-w-6xl rounded-lg px-4 sm:px-6 md:px-10 py-8">
          {/* HERO */}
          <div className="space-y-4">
            <h1 className="font-bold text-4xl sm:text-5xl">
              Westmore Adult Program
            </h1>

            <button
              type="button"
              onClick={() => openModal(HERO_IMAGE)}
              className="w-full text-left"
            >
              <img
                src={HERO_IMAGE}
                alt="Adult Brazilian Jiu Jitsu training"
                className="w-full rounded-lg border border-white/10 object-cover max-h-[360px]"
              />
            </button>

            <div className="space-y-3 text-base sm:text-lg text-white/90">
              <p>
                Westmore Brazilian Jiu Jitsu offers structured, principle-driven
                training for adults of all experience levels. Instruction
                emphasizes technical efficiency, positional understanding, and
                calm decision making under pressure.
              </p>
            </div>
          </div>

          <Section
            title="Training Philosophy"
            image={SECTION_IMAGES.philosophy}
            onImageClick={openModal}
          >
            <p>
              Training focuses on positional control, leverage, and repeatable
              solutions rather than strength or athleticism. Students learn
              principles that scale across positions rather than memorizing long
              technique chains.
            </p>
          </Section>

          <Section
            title="Training Emphases"
            image={SECTION_IMAGES.trainingEmphasis}
            onImageClick={openModal}
          >
            <p>
              Fundamentals, live application, competition preparation, and
              footlock systems are integrated into training. Footlock strategies
              are taught as positional systems emphasizing control, safety, and
              defensive awareness.
            </p>
          </Section>

          <Section
            title="Gi and No-Gi Training"
            image={SECTION_IMAGES.giNoGi}
            onImageClick={openModal}
          >
            <p>
              Westmore offers both gi and no-gi training to support well-rounded
              grappling development across rule sets and competitive formats.
            </p>
          </Section>

          {/* SCHEDULE */}
          <div className="mt-10 space-y-4">
            <h2 className="font-bold text-2xl sm:text-3xl underline">
              Adult Class Schedule
            </h2>

            <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-4">
              <h3 className="font-semibold text-lg">Gi Classes</h3>
              <ScheduleRow
                day="Monday / Wednesday / Friday"
                time="12:00–1:15 PM"
              />
              <ScheduleRow
                day="Monday / Wednesday / Friday"
                time="6:00–7:30 PM"
              />
              <ScheduleRow
                day="Friday"
                time="9:00 AM"
                detail="Fundamentals Gi"
              />

              <h3 className="font-semibold text-lg pt-4">No-Gi Classes</h3>
              <ScheduleRow
                day="Tuesday"
                time="6:00–7:30 PM"
                detail="Wrestling-Based No-Gi"
              />
              <ScheduleRow day="Thursday" time="6:00–7:30 PM" detail="No-Gi" />

              <div className="pt-2 text-white/80 text-sm">
                Weekend training available by reservation.
              </div>
            </div>
          </div>

          {/* GALLERY */}
          <div className="mt-10">
            <div ref={galleryRef} className="flex gap-3 overflow-x-auto pb-2">
              {GALLERY_IMAGES.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => openModal(img)}
                  className="shrink-0"
                >
                  <img
                    src={img}
                    alt={`Gallery image ${idx + 1}`}
                    className="h-24 w-32 sm:h-28 sm:w-40 object-cover rounded-lg border border-white/10"
                    />
                </button>
              ))}
            </div>
          </div>

          {/* MODAL */}
          {activeImg && (
            <div
              className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              <div
                className="max-w-5xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-end pb-2">
                  <button
                    onClick={closeModal}
                    className="px-3 py-2 bg-black border border-white rounded-lg text-sm"
                  >
                    Close
                  </button>
                </div>
                <img
                    src={activeImg}
                    alt="Expanded"
                    className="w-full max-h-[80vh] object-contain rounded-lg border border-white"
                    />
              </div>
            </div>
          )}
        </div>
      </div>
    </m.div>
  );
};

function Section({ title, children, image, onImageClick }) {
  return (
    <div className="mt-10">
      <h2 className="font-bold text-2xl sm:text-3xl underline">{title}</h2>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-white/90 space-y-3 text-base sm:text-lg">
          {children}
        </div>

        <button
          onClick={() => onImageClick(image)}
          className="w-full text-left"
        >
          <img
            src={image}
            alt={title}
            className="w-full h-[240px] object-cover rounded-lg border border-white/10"
          />
        </button>
      </div>
    </div>
  );
}

function ScheduleRow({ day, time, detail }) {
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 border-b border-white/10 pb-2">
      <div className="font-semibold">{day}</div>
      <div>{time}</div>
      {detail && <div className="text-white/80">{detail}</div>}
    </div>
  );
}

export default AdultProgram;
